import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineFileExcel, AiOutlineFilePdf, AiOutlineFileWord, AiOutlineReload, AiOutlineClose } from "react-icons/ai";
import { useDocumentTypeService, useVersionService } from "../services/api";
import { useArbolDocumentalService } from "../services/api";
import useUser from "../hooks/useUser";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, HeadingLevel, WidthType, AlignmentType, BorderStyle } from "docx";
import { saveAs } from "file-saver";
import { formatDateTime } from "../utils/dateUtils";
import { ENV } from "../config/env";
import { buildTreeFromFlat } from "../components/DocTreeSidebar/DocTreeSidebar";

// Versión de la UI (sincronizada con package.json)
const UI_VERSION = "1.4.0-RC3";

/**
 * Página de estadísticas de series documentales.
 * Muestra conteos de documentos por estado para cada serie,
 * con carga lazy de los conteos y posibilidad de exportar a PDF, Excel o Word.
 */
const SeriesStatsPage = () => {
  const navigate = useNavigate();
  const documentTypeService = useDocumentTypeService();
  const arbolService = useArbolDocumentalService();
  const versionService = useVersionService();
  const { user } = useUser();

  const [series, setSeries] = useState([]);
  const [counts, setCounts] = useState({}); // { [id]: SerieCountsResponse | 'loading' | 'error' }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiVersion, setApiVersion] = useState("N/D");
  // Folders del doctree, para armar la jerarquía visual junto con `series`.
  const [folders, setFolders] = useState([]);
  // Folders explícitamente colapsados por el usuario. Default abierto (como
  // /admin/series y /control-de-calidad). Persistido en localStorage.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem("boveda.stats.collapsed");
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });
  const toggleCollapsed = (id) => {
    setCollapsed(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = true;
      try { localStorage.setItem("boveda.stats.collapsed", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Ref para saber si el componente sigue montado
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Carga inicial: lista de series, folders del árbol y versión de la API.
  useEffect(() => {
    loadSeries();
    loadFolders();
    loadApiVersion();
  }, []);

  const loadFolders = async () => {
    try {
      const response = await documentTypeService.getFolders();
      if (mountedRef.current) {
        setFolders(Array.isArray(response?.data) ? response.data : []);
      }
    } catch {
      // Sin folders, el árbol queda con series como raíces (degrada bien).
    }
  };

  // Árbol jerárquico folders + series. Memoizado para no recalcular al cambiar counts.
  const seriesTree = useMemo(
    () => buildTreeFromFlat(folders, series),
    [folders, series]
  );

  // Aplana el árbol depth-first a una lista de filas con `depth` para indentación
  // en la tabla. Folders se muestran como rows-header (sin counts), series como
  // rows-data con sus 6 columnas numéricas. Mantiene el orden alfabético interno
  // que aplica buildTreeFromFlat. Si un folder está colapsado, sus descendientes
  // no aparecen en la lista. Cada folder lleva `hasChildren` para decidir si
  // pintamos el chevron clickeable.
  const flatRows = useMemo(() => {
    const rows = [];
    const walk = (nodes, depth) => {
      for (const n of nodes) {
        const isFolder = !n.isSeries;
        const hasChildren = isFolder && Array.isArray(n.folders) && n.folders.length > 0;
        rows.push({
          id: n.id,
          name: n.name,
          isSeries: !isFolder,
          depth,
          hasChildren,
          data: n._data,
        });
        if (hasChildren && !collapsed[n.id]) {
          walk(n.folders, depth + 1);
        }
      }
    };
    walk(seriesTree, 0);
    return rows;
  }, [seriesTree, collapsed]);

  const loadApiVersion = async () => {
    try {
      const version = await versionService.getApiVersion();
      if (mountedRef.current) setApiVersion(version);
    } catch {
      // silenciar: el footer muestra N/D
    }
  };

  const loadSeries = async () => {
    setLoading(true);
    setError(null);
    setCounts({});
    try {
      const response = await documentTypeService.getSeriesStats();
      const seriesList = response?.data || [];
      if (mountedRef.current) {
        setSeries(seriesList);
        setLoading(false);
        // Disparar carga lazy de conteos para cada serie
        seriesList.forEach(s => fetchCounts(s.id));
      }
    } catch (err) {
      if (mountedRef.current) {
        setError("No se pudieron cargar las series documentales. Intente nuevamente.");
        setLoading(false);
      }
    }
  };

  const fetchCounts = useCallback(async (id) => {
    setCounts(prev => ({ ...prev, [id]: "loading" }));
    try {
      const response = await arbolService.getSerieCounts(id);
      if (mountedRef.current) {
        setCounts(prev => ({ ...prev, [id]: response?.data || null }));
      }
    } catch {
      if (mountedRef.current) {
        setCounts(prev => ({ ...prev, [id]: "error" }));
      }
    }
  }, [arbolService]);

  // ─── Helpers para los datos del reporte ──────────────────────────────────────

  const getReportTimestamp = () =>
    formatDateTime(new Date(), { withSeconds: false });

  const getReportUser = () => user?.profile?.preferred_username || user?.profile?.name || "Usuario desconocido";

  const getTableRows = () =>
    series.map(s => {
      const c = counts[s.id];
      const isLoading = c === "loading";
      const isError = c === "error";
      const val = (field) => {
        if (isLoading) return "…";
        if (isError || !c) return "-";
        return (c[field] ?? 0).toString();
      };
      return {
        name: s.name || s.id,
        total: val("totalDocCount"),
        accessible: val("accessiblePublishedDocCount"),
        ocrPending: val("ocrPendingDocCount"),
        reviewPending: val("reviewPendingDocCount"),
        approvalPending: val("approvalPendingDocCount"),
        published: val("publishedDocCount"),
      };
    });

  // ─── Exportación PDF ─────────────────────────────────────────────────────────

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const ts = getReportTimestamp();
    const userName = getReportUser();
    const rows = getTableRows();

    // Header
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`${ENV.APP_NAME} - ${ENV.ORG_SHORT}`, 14, 16);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Estadísticas de Series Documentales", 14, 23);
    doc.setFontSize(9);
    doc.text(`Fecha de generación: ${ts}`, 14, 30);
    doc.text(`Usuario: ${userName}`, 14, 36);

    // Tabla
    autoTable(doc, {
      startY: 42,
      head: [[
        "Serie Documental", "Total", "Accesibles",
        "Pend. OCR", "Pend. Revisión", "Pend. Aprobación", "Publicados"
      ]],
      body: rows.map(r => [
        r.name, r.total, r.accessible,
        r.ocrPending, r.reviewPending, r.approvalPending, r.published
      ]),
      headStyles: { fillColor: [0, 70, 139], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 70 } },
    });

    // Footer en cada página
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.text(
        `UI: v${UI_VERSION} | API: v${apiVersion} | Página ${i} de ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 8,
        { align: "center" }
      );
    }

    doc.save(`estadisticas-series-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // ─── Exportación Excel ───────────────────────────────────────────────────────

  const exportExcel = () => {
    const ts = getReportTimestamp();
    const userName = getReportUser();
    const rows = getTableRows();

    const wb = XLSX.utils.book_new();

    const wsData = [
      [`${ENV.APP_NAME} - ${ENV.ORG_SHORT}`],
      ["Estadísticas de Series Documentales"],
      [`Fecha de generación: ${ts}`],
      [`Usuario: ${userName}`],
      [], // Fila vacía
      ["Serie Documental", "Total", "Accesibles", "Pend. OCR", "Pend. Revisión", "Pend. Aprobación", "Publicados"],
      ...rows.map(r => [r.name, r.total, r.accessible, r.ocrPending, r.reviewPending, r.approvalPending, r.published]),
      [],
      [`UI: v${UI_VERSION} | API: v${apiVersion}`],
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 40 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, "Estadísticas");
    XLSX.writeFile(wb, `estadisticas-series-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ─── Exportación Word ────────────────────────────────────────────────────────

  const exportWord = async () => {
    const ts = getReportTimestamp();
    const userName = getReportUser();
    const rows = getTableRows();

    const headerColor = "00468B";
    const colWidths = [3000, 1200, 1400, 1400, 1600, 1800, 1400];
    const headers = ["Serie Documental", "Total", "Accesibles", "Pend. OCR", "Pend. Revisión", "Pend. Aprobación", "Publicados"];

    const makeCell = (text, bold = false, shade = null) => new TableCell({
      children: [new Paragraph({
        children: [new TextRun({ text: String(text), bold, color: shade ? "FFFFFF" : "000000", size: 18 })],
        alignment: AlignmentType.CENTER,
      })],
      shading: shade ? { fill: shade } : undefined,
      margins: { top: 60, bottom: 60, left: 80, right: 80 },
    });

    const headerRow = new TableRow({
      children: headers.map((h, i) => makeCell(h, true, headerColor)),
      tableHeader: true,
    });

    const dataRows = rows.map((r, idx) => new TableRow({
      children: [
        makeCell(r.name, false, idx % 2 === 0 ? null : "F5F8FF"),
        makeCell(r.total),
        makeCell(r.accessible),
        makeCell(r.ocrPending),
        makeCell(r.reviewPending),
        makeCell(r.approvalPending),
        makeCell(r.published),
      ],
    }));

    const doc = new Document({
      sections: [{
        properties: { page: { size: { orientation: "LANDSCAPE" } } },
        children: [
          new Paragraph({ text: `${ENV.APP_NAME} - ${ENV.ORG_SHORT}`, heading: HeadingLevel.HEADING_1 }),
          new Paragraph({ text: "Estadísticas de Series Documentales", heading: HeadingLevel.HEADING_2 }),
          new Paragraph({ children: [new TextRun({ text: `Fecha de generación: ${ts}`, size: 20 })] }),
          new Paragraph({ children: [new TextRun({ text: `Usuario: ${userName}`, size: 20 })] }),
          new Paragraph({ text: "" }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            columnWidths: colWidths,
            rows: [headerRow, ...dataRows],
          }),
          new Paragraph({ text: "" }),
          new Paragraph({
            children: [new TextRun({ text: `UI: v${UI_VERSION} | API: v${apiVersion}`, size: 16, italics: true, color: "888888" })],
            alignment: AlignmentType.CENTER,
          }),
        ],
      }],
    });

    const buffer = await Packer.toBlob(doc);
    saveAs(buffer, `estadisticas-series-${new Date().toISOString().slice(0, 10)}.docx`);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const allLoaded = series.length > 0 && series.every(s => counts[s.id] && counts[s.id] !== "loading");

  return (
    <div style={styles.container}>
      {/* Encabezado de la página */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h1 style={styles.title}>Estadísticas de Series Documentales</h1>
            <p style={styles.subtitle}>
              Conteos de documentos por estado para cada serie del sistema
            </p>
          </div>
        </div>

        <div style={styles.exportButtons}>
          <button
            style={styles.exportBtn}
            onClick={exportPdf}
            disabled={loading || !!error}
            title="Exportar a PDF"
          >
            <AiOutlineFilePdf size={14} style={styles.exportIconPdf} />
            <span>PDF</span>
          </button>
          <button
            style={styles.exportBtn}
            onClick={exportExcel}
            disabled={loading || !!error}
            title="Exportar a Excel"
          >
            <AiOutlineFileExcel size={14} style={styles.exportIconExcel} />
            <span>Excel</span>
          </button>
          <button
            style={styles.exportBtn}
            onClick={exportWord}
            disabled={loading || !!error}
            title="Exportar a Word"
          >
            <AiOutlineFileWord size={14} style={styles.exportIconWord} />
            <span>Word</span>
          </button>
          <button
            style={styles.reloadBtn}
            onClick={loadSeries}
            disabled={loading}
            title="Actualizar datos"
          >
            <AiOutlineReload size={18} style={loading ? styles.spinning : {}} />
          </button>
          <button style={styles.backButton} onClick={() => navigate("/")} title="Volver al inicio">
            <AiOutlineClose style={styles.backIcon} />
            Cerrar
          </button>
        </div>
      </div>

      {/* Estado de carga / error */}
      {loading && (
        <div style={styles.stateBox}>
          <div style={styles.spinner} />
          <p style={styles.stateText}>Cargando series documentales…</p>
        </div>
      )}

      {!loading && error && (
        <div style={styles.errorBox}>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.retryBtn} onClick={loadSeries}>Reintentar</button>
        </div>
      )}

      {/* Tabla de estadísticas con jerarquía visual: folders como sub-headers,
          series anidadas debajo con indentación según la profundidad del árbol. */}
      {!loading && !error && (
        <>
          {series.length === 0 ? (
            <div style={styles.stateBox}>
              <p style={styles.stateText}>No hay series documentales registradas.</p>
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Serie Documental</th>
                    <th style={{ ...styles.th, ...styles.thNumber }}>Total</th>
                    <th style={{ ...styles.th, ...styles.thNumber }}>Accesibles</th>
                    <th style={{ ...styles.th, ...styles.thNumber }}>Pend. OCR</th>
                    <th style={{ ...styles.th, ...styles.thNumber }}>Pend. Revisión</th>
                    <th style={{ ...styles.th, ...styles.thNumber }}>Pend. Aprobación</th>
                    <th style={{ ...styles.th, ...styles.thNumber }}>Publicados</th>
                  </tr>
                </thead>
                <tbody>
                  {flatRows.map((row, idx) => {
                    const indent = { paddingLeft: `${12 + row.depth * 20}px` };
                    // Folder row: una fila de cabecera por folder, las celdas
                    // numéricas quedan vacías porque las stats son por serie.
                    if (!row.isSeries) {
                      const isCollapsed = !!collapsed[row.id];
                      const clickable = row.hasChildren;
                      return (
                        <tr key={`f-${row.id}`}
                            style={{ ...styles.folderRow, ...(clickable ? styles.folderRowClickable : {}) }}
                            onClick={clickable ? () => toggleCollapsed(row.id) : undefined}
                            title={clickable ? (isCollapsed ? 'Expandir' : 'Colapsar') : undefined}>
                          <td style={{ ...styles.tdFolderName, ...indent }} colSpan={7}>
                            <span style={styles.folderIcon}>
                              {clickable ? (isCollapsed ? '▸' : '▾') : '·'}
                            </span>
                            {' '}{row.name}
                          </td>
                        </tr>
                      );
                    }
                    // Series row: igual al original, pero con indentación.
                    const c = counts[row.id];
                    const isLoadingRow = !c || c === "loading";
                    const isErrorRow = c === "error";
                    const cell = (field) => {
                      if (isLoadingRow) return <span style={styles.loadingPill}>…</span>;
                      if (isErrorRow) return <span style={styles.errorPill}>-</span>;
                      return <span style={styles.countBadge}>{c[field] ?? 0}</span>;
                    };
                    return (
                      <tr key={`s-${row.id}`} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                        <td style={{ ...styles.tdName, ...indent }}>
                          <span style={styles.serieId}>{row.id}</span>
                          <span style={styles.serieName}>{row.name}</span>
                        </td>
                        <td style={styles.tdNumber}>{cell("totalDocCount")}</td>
                        <td style={styles.tdNumber}>{cell("accessiblePublishedDocCount")}</td>
                        <td style={styles.tdNumber}>{cell("ocrPendingDocCount")}</td>
                        <td style={styles.tdNumber}>{cell("reviewPendingDocCount")}</td>
                        <td style={styles.tdNumber}>{cell("approvalPendingDocCount")}</td>
                        <td style={styles.tdNumber}>{cell("publishedDocCount")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pie de página informativo */}
          <div style={styles.footer}>
            <span>UI: v{UI_VERSION}</span>
            <span style={styles.footerSep}>|</span>
            <span>API: v{apiVersion}</span>
            {!allLoaded && (
              <>
                <span style={styles.footerSep}>|</span>
                <span style={styles.footerLoading}>Cargando conteos…</span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Keyframe para el spinner (se inyecta una sola vez) ──────────────────────
const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`;
document.head.appendChild(styleSheet);

const styles = {
  container: {
    padding: "24px",
    backgroundColor: "#F8F9FA",
    minHeight: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    gap: "16px",
    flexWrap: "wrap",
  },
  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "#6B7280",
    color: "#FFFFFF",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },
  backIcon: {
    fontSize: "16px",
  },
  title: {
    margin: "0 0 4px 0",
    fontSize: "22px",
    fontWeight: "bold",
    color: "#00468B",
  },
  subtitle: {
    margin: 0,
    fontSize: "14px",
    color: "#6B7280",
  },
  exportButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  // Botones de export: estilo outline + neutro, con un toque sutil de color en
  // el icono según el formato. Más discretos que el sólido vivo de antes.
  exportBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "5px 10px",
    border: "1px solid #D1D5DB",
    borderRadius: "5px",
    background: "#FFFFFF",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
    color: "#374151",
    transition: "background-color 0.15s, border-color 0.15s",
  },
  exportBtnPdf:   { color: "#374151" },
  exportBtnExcel: { color: "#374151" },
  exportBtnWord:  { color: "#374151" },
  exportIconPdf:   { color: "#B91C1C" },
  exportIconExcel: { color: "#15803D" },
  exportIconWord:  { color: "#1D4ED8" },
  reloadBtn: {
    display: "flex",
    alignItems: "center",
    padding: "8px",
    background: "#E5E7EB",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    color: "#374151",
  },
  spinning: {
    animation: "spin 1s linear infinite",
  },
  stateBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    gap: "16px",
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "4px solid #E5E7EB",
    borderTop: "4px solid #00468B",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  stateText: {
    color: "#6B7280",
    fontSize: "16px",
    margin: 0,
  },
  errorBox: {
    backgroundColor: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: "8px",
    padding: "24px",
    textAlign: "center",
  },
  errorText: {
    color: "#B91C1C",
    margin: "0 0 16px 0",
    fontSize: "15px",
  },
  retryBtn: {
    backgroundColor: "#00468B",
    color: "#FFF",
    border: "none",
    borderRadius: "6px",
    padding: "8px 20px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  tableWrapper: {
    backgroundColor: "#FFFFFF",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    overflowX: "auto",
  },
  folderRow: {
    backgroundColor: "#F3F4F6",
    borderTop: "1px solid #E5E7EB",
  },
  folderRowClickable: {
    cursor: "pointer",
    userSelect: "none",
  },
  tdFolderName: {
    padding: "8px 12px",
    fontWeight: "600",
    fontSize: "13px",
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  folderIcon: {
    color: "#9CA3AF",
    marginRight: "6px",
    display: "inline-block",
    width: "12px",
    textAlign: "center",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  th: {
    backgroundColor: "#00468B",
    color: "#FFFFFF",
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "bold",
    fontSize: "13px",
    whiteSpace: "nowrap",
    borderBottom: "2px solid #003570",
  },
  thNumber: {
    textAlign: "center",
  },
  rowEven: { backgroundColor: "#FFFFFF" },
  rowOdd: { backgroundColor: "#F0F5FF" },
  tdName: {
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
    verticalAlign: "middle",
  },
  serieId: {
    display: "block",
    fontSize: "11px",
    color: "#9CA3AF",
    fontFamily: "monospace",
  },
  serieName: {
    display: "block",
    color: "#111827",
    fontWeight: "500",
  },
  tdNumber: {
    padding: "12px 16px",
    textAlign: "center",
    borderBottom: "1px solid #E5E7EB",
    verticalAlign: "middle",
  },
  countBadge: {
    display: "inline-block",
    backgroundColor: "#EFF6FF",
    color: "#1D4ED8",
    borderRadius: "12px",
    padding: "2px 10px",
    fontWeight: "600",
    fontSize: "13px",
    minWidth: "32px",
  },
  loadingPill: {
    display: "inline-block",
    backgroundColor: "#F3F4F6",
    color: "#9CA3AF",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "13px",
  },
  errorPill: {
    display: "inline-block",
    backgroundColor: "#FEE2E2",
    color: "#B91C1C",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "13px",
  },
  footer: {
    marginTop: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#9CA3AF",
    fontSize: "12px",
  },
  footerSep: {
    color: "#D1D5DB",
  },
  footerLoading: {
    color: "#6B7280",
    fontStyle: "italic",
  },
};

export default SeriesStatsPage;
